/**
 * Shared virtual-clock control surface used by both per-league Simulation
 * tabs (Game Data → EPL/WC → Simulation). The clock itself is global —
 * one instance per backend process — so each league sees the same
 * readout and the same Set / Speed / Advance / Reset buttons. Lifted
 * verbatim from the previous System → Simulation Clock tab; the only
 * difference is the surrounding tab no longer hides the controls in
 * production (the per-league tab handles that gate at the top level so
 * the rest of its content — round status, fixture list — still renders).
 */

import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import {
  useAdminClock,
  useAdvanceClock,
  useResetClock,
  useSetClock,
  useSetClockSpeed,
} from '../system/api';
import { formatTimestamp, timeUntil } from '../system/shared';

// Speed presets are intentionally non-linear: real-time, "one simulated
// minute per second", and "one simulated hour per second". Maps cleanly
// to how operators reason about virtual clock advancement.
const SPEED_PRESETS = [
  { value: 0, label: 'PAUSE' },
  { value: 1, label: '1x' },
  { value: 60, label: '60x' },
  { value: 3600, label: '3600x' },
];

export function SimulationControls() {
  const { data: clock, isLoading, error } = useAdminClock();

  const setClock = useSetClock();
  const setSpeed = useSetClockSpeed();
  const advance = useAdvanceClock();
  const reset = useResetClock();

  const [setIso, setSetIso] = useState<string>('');
  const [actionError, setActionError] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="rounded-md border bg-card px-4 py-12 text-center text-sm text-muted-foreground">
        Loading clock state…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-6 text-sm text-destructive">
        {error instanceof Error ? error.message : 'Failed to load clock state.'}
      </div>
    );
  }

  if (!clock) return null;

  const speed = clock.speed;
  const isPaused = speed === 0;
  const nextEvent = clock.next_event;

  const datetimeInputValue = setIso ? setIso : isoToLocalInput(clock.server_time);

  async function withAction<T>(fn: () => Promise<T>): Promise<void> {
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Action failed');
    }
  }

  async function handleSet() {
    if (!datetimeInputValue) return;
    const local = new Date(datetimeInputValue);
    if (Number.isNaN(local.getTime())) {
      setActionError('Invalid date/time');
      return;
    }
    await withAction(() => setClock.mutateAsync(local.toISOString()));
    setSetIso('');
  }

  return (
    <div className="space-y-6">
      {/* ─── Status row ────────────────────────────────────────────────── */}
      <div className="rounded-md border bg-card px-4 py-4">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Virtual time
            </div>
            <div className="font-mono text-base font-medium">
              {formatTimestamp(clock.server_time)}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Speed</div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-base font-medium">{speed}x</span>
              {isPaused ? (
                <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">
                  PAUSED
                </Badge>
              ) : null}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Simulation mode
            </div>
            <div className="font-mono text-base">{clock.simulation_mode}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Real time
            </div>
            <div className="font-mono text-base text-muted-foreground">
              {formatTimestamp(clock.real_time)}
            </div>
          </div>
          {nextEvent ? (
            <div className="ml-auto">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Next event
              </div>
              <div className="text-sm">
                <span className="font-medium">{nextEvent.label}</span>
                <span className="ml-2 text-muted-foreground">
                  {timeUntil(nextEvent.at)} · {formatTimestamp(nextEvent.at)}
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* ─── Speed presets ───────────────────────────────────────────── */}
      <ControlGroup title="Speed">
        <div className="flex flex-wrap gap-2">
          {SPEED_PRESETS.map((p) => (
            <Button
              key={p.value}
              variant={p.value === speed ? 'default' : 'outline'}
              size="sm"
              disabled={setSpeed.isPending}
              onClick={() => withAction(() => setSpeed.mutateAsync(p.value))}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </ControlGroup>

      {/* ─── Advance ─────────────────────────────────────────────────── */}
      <ControlGroup title="Advance">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={advance.isPending || !nextEvent}
            onClick={() => withAction(() => advance.mutateAsync({ to_next_event: true }))}
          >
            Next event
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={advance.isPending}
            onClick={() => withAction(() => advance.mutateAsync({ hours: 1 }))}
          >
            +1h
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={advance.isPending}
            onClick={() => withAction(() => advance.mutateAsync({ hours: 6 }))}
          >
            +6h
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={advance.isPending}
            onClick={() => withAction(() => advance.mutateAsync({ days: 1 }))}
          >
            +1d
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={advance.isPending}
            onClick={() => withAction(() => advance.mutateAsync({ days: 7 }))}
          >
            +7d
          </Button>
        </div>
      </ControlGroup>

      {/* ─── Set absolute time ───────────────────────────────────────── */}
      <ControlGroup title="Set time">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="datetime-local"
            className="h-9 max-w-xs"
            value={datetimeInputValue}
            onChange={(e) => setSetIso(e.target.value)}
          />
          <Button
            size="sm"
            disabled={setClock.isPending || !datetimeInputValue}
            onClick={handleSet}
          >
            Jump to time
          </Button>
          <span className="text-xs text-muted-foreground">
            (interpreted as your local timezone)
          </span>
        </div>
      </ControlGroup>

      {/* ─── Reset ───────────────────────────────────────────────────── */}
      <ControlGroup title="Reset">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="destructive"
            disabled={reset.isPending}
            onClick={() => {
              if (
                window.confirm(
                  'Reset clock to 1 day before earliest fixture and wipe played state?',
                )
              ) {
                void withAction(() => reset.mutateAsync());
              }
            }}
          >
            Reset clock + wipe played state
          </Button>
          <span className="text-xs text-muted-foreground">
            Anchors to 1d before earliest fixture, speed=1, clears stats / results /
            round + competition transitions across both leagues.
          </span>
        </div>
      </ControlGroup>

      {actionError ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-2 text-sm text-destructive">
          {actionError}
        </div>
      ) : null}
    </div>
  );
}

function ControlGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
